import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { ShoppingCart, Minus, Plus } from 'lucide-react';
import { VariantSelector } from './VariantSelector';
import { PriceBlock, pickDefaultVariant } from './pricing';
import { getImageUrl } from '../utils/helpers';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';
import { trackAddToCart } from '../utils/analytics';

const variantLabel = (v) => [v?.size, v?.color].filter(Boolean).join(' / ');

export const VariantSelectionModal = ({ product, open, onOpenChange }) => {
  const { addToCart } = useCart();
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (open) { setSelected(pickDefaultVariant(product?.variants)); setQty(1); }
  }, [open, product]);

  if (!product) return null;
  const stock = Number(selected?.stock || 0);
  const pid = product._id || product.id;

  const confirm = () => {
    if (!selected || stock < 1) return;
    addToCart(product, qty, selected);
    trackAddToCart({ ...product, price: Number(selected.price) }, qty);
    toast({ title: 'Agregado al carrito', description: `${product.title} (${variantLabel(selected)})` });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onClick={e => e.stopPropagation()}
        className="p-0 gap-0 overflow-hidden max-w-md max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:max-w-full"
        data-testid="variant-modal">
        <div className="flex gap-3 p-4 border-b">
          <img src={getImageUrl(product.images?.[0] || product.image)} alt={product.title} className="w-20 h-20 rounded-lg object-cover bg-gray-100 shrink-0" />
          <div className="min-w-0 pr-6">
            <DialogTitle className="text-sm font-medium text-gray-700 line-clamp-2">{product.title}</DialogTitle>
            <DialogDescription className="sr-only">Elige la variante antes de agregar al carrito</DialogDescription>
            <PriceBlock price={Number(selected?.price ?? product.price)} compare={selected ? selected.compare_at_price : product.compare_at_price} size="sm" testid="variant-modal-price" />
          </div>
        </div>
        <div className="p-4 max-h-[50vh] overflow-y-auto">
          <VariantSelector variants={product.variants} selected={selected} onSelect={v => { setSelected(v); setQty(1); }} large testidPrefix="modal-variant" />
          <p className={`text-xs ${stock > 0 ? 'text-gray-500' : 'text-red-600 font-medium'}`} data-testid="variant-modal-stock">
            {selected ? (stock > 0 ? `${stock} disponibles · ${variantLabel(selected)}` : 'Sin stock en esta combinación') : 'Elige una opción'}
          </p>
        </div>
        <div className="flex items-center gap-3 p-4 border-t bg-gray-50">
          <div className="flex items-center rounded-full border bg-white">
            <button type="button" className="w-11 h-11 flex items-center justify-center disabled:opacity-30" disabled={qty <= 1} onClick={() => setQty(q => q - 1)} data-testid="variant-modal-qty-minus"><Minus className="w-4 h-4" /></button>
            <span className="w-8 text-center font-semibold" data-testid="variant-modal-qty">{qty}</span>
            <button type="button" className="w-11 h-11 flex items-center justify-center disabled:opacity-30" disabled={qty >= stock} onClick={() => setQty(q => q + 1)} data-testid="variant-modal-qty-plus"><Plus className="w-4 h-4" /></button>
          </div>
          <Button onClick={confirm} disabled={!selected || stock < 1} className="flex-1 h-11 rounded-full bg-zl-orange hover:bg-zl-orange-600 text-white font-semibold" data-testid={`variant-modal-add-${pid}`}>
            <ShoppingCart className="w-4 h-4 mr-2" /> Agregar al carrito
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
